import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  styled,
  Typography,
} from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { ENTITY } from "constants/entities";
import { STATUS_CODES } from "constants/index";
import { ARTICLES_URL } from "constants/urls";
import {
  normalize,
  normalizeHeader,
  normalizeSchemaKeys,
  sanitizeExcelValue,
} from "helpers";
import { validateSchema } from "helpers/schemaValidator";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { useRef, useState } from "react";
import { MdCancel, MdCheck, MdCloudUpload } from "react-icons/md";
import { Client, Container, ExcelCellProps, NewArticle, Unit } from "types";
import { read, utils } from "xlsx";

type Props = {
  clients: Client[];
  containers: Container[];
  units: Unit[];
};

type NewArticleRow = Record<string, any>;

const SHEET_NAME = "ARTICULOS";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function FileImportArticles({
  clients,
  containers,
  units,
}: Props) {
  const [uploadedArticles, setUploadedArticles] = useState<NewArticle[]>([]);
  const [listOfErrors, setListOfErrors] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetInput, setResetInput] = useState(Math.random().toString(36));
  const [error, setError] = useState(false);
  const fileUploadRef = useRef<HTMLInputElement | null>(null);
  const { addSessionData, updateSessionData, groups } = useSession();

  const { createData: createOrUpdateArticlesMassive } = useCrud<any>(
    `${ARTICLES_URL}/massive`,
    {
      disableAutoFetch: true,
    },
  );

  const { showSnackBar } = useToast();

  const articlesSchema: Record<string, ExcelCellProps> = {
    Descripcion: { type: "string", required: true },
    Codigo: { type: "string", required: true },
    Peso: { type: "number", required: true },
    Ancho: { type: "number", required: false },
    Alto: { type: "number", required: false },
    Largo: { type: "number", required: false },
    "Cantidad por Contenedor": { type: "number", required: true },
    "Unidades por pallet": { type: "number", required: true },
    Observaciones: { type: "string", required: false },
    "Unidad operativa": {
      type: "string",
      required: true,
      associations: units.map((u) => sanitizeExcelValue(u.description)),
    },
    Contenedor: {
      type: "string",
      required: true,
      associations: containers.map((c) => sanitizeExcelValue(c.description)),
    },
    Cliente: {
      type: "string",
      required: true,
      associations: clients.map((c) => sanitizeExcelValue(`${c.code}`)),
    },
    "Requiere fecha vencimiento": {
      type: "string",
      required: false,
    },
    "Cantidad por contenedor estándar": { type: "string", required: true },
    Grupo: { type: "string", required: false },
    FCE: { type: "number", required: true },
  };

  const CANONICAL_MAP: Record<string, string> = {
    descripcion: "Descripcion",
    codigo: "Codigo",
    peso: "Peso",
    ancho: "Ancho",
    alto: "Alto",
    largo: "Largo",
    "cantidad por contenedor": "Cantidad por Contenedor",
    "unidades por pallet": "Unidades por pallet",
    observaciones: "Observaciones",
    grupo: "Grupo",
    "unidad operativa": "Unidad operativa",
    contenedor: "Contenedor",
    cliente: "Cliente",
    "requiere fecha vencimiento": "Requiere fecha vencimiento",
    "cantidad por contenedor estandar": "Cantidad por contenedor estándar",
    fce: "FCE",
  };

  function parseExcelYesNo(value: any, defaultValue = false): boolean {
    if (value === undefined || value === null || String(value).trim() === "") {
      return defaultValue;
    }

    const v = String(value).trim().toLowerCase();

    if (["si", "sí"].includes(v)) return true;
    if (["no"].includes(v)) return false;

    throw new Error(`Valor inválido: "${value}". Usar SI o NO.`);
  }

  function parseGroupCodesCell(value: any): string[] {
    if (value === undefined || value === null) return [];

    const raw = String(value).trim();
    if (!raw) return [];

    const codes = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    return Array.from(new Set(codes));
  }

  async function createArticleStructure(rows: NewArticleRow[]) {
    const errors: string[] = [];
    const transformed: NewArticle[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const line = i + 1;

      const excelClient = String(row["Cliente"]).trim();
      const client = clients.find((c) => String(c.code).trim() === excelClient);

      if (!client) {
        errors.push(`Fila ${line}: Cliente "${row["Cliente"]}" no encontrado.`);
        continue;
      }

      const excelUnit = String(row["Unidad operativa"]).trim().toLowerCase();
      const unit = units.find(
        (u) => String(u.description).trim().toLowerCase() === excelUnit,
      );

      if (!unit) {
        errors.push(
          `Fila ${line}: Unidad operativa "${row["Unidad operativa"]}" no válida.`,
        );
        continue;
      }

      const excelContainer = String(row["Contenedor"]).trim().toLowerCase();
      const container = containers.find(
        (c) => String(c.description).trim().toLowerCase() === excelContainer,
      );

      if (!container) {
        errors.push(
          `Fila ${line}: Contenedor "${row["Contenedor"]}" no encontrado.`,
        );
        continue;
      }

      const groupCodes = parseGroupCodesCell(row["Grupo"]);

      const groupIds: number[] = [];
      if (groupCodes.length > 0) {
        for (const code of groupCodes) {
          const g = groups.find(
            (gr) =>
              gr.client_id === client.id && String(gr.code).trim() === code,
          );

          if (!g) {
            errors.push(
              `Fila ${line}: Grupo "${code}" no existe para el cliente ${client.code}.`,
            );
            continue;
          }

          groupIds.push(g.id);
        }
      }

      const lastErrorIsThisLine = errors.some((e) =>
        e.startsWith(`Fila ${line}: Grupo`),
      );
      if (lastErrorIsThisLine) continue;

      transformed.push({
        description: row["Descripcion"],
        code: row["Codigo"],
        weight: Number(row["Peso"]),
        width:
          row["Ancho"] === "" || row["Ancho"] == null
            ? null
            : Number(row["Ancho"]),
        height:
          row["Alto"] === "" || row["Alto"] == null
            ? null
            : Number(row["Alto"]),
        long:
          row["Largo"] === "" || row["Largo"] == null
            ? null
            : Number(row["Largo"]),
        amount: 0,
        amount_per_container: Number(row["Cantidad por Contenedor"]),
        units_per_pallet: Number(row["Unidades por pallet"]),
        observation: String(row["Observaciones"] ?? "").trim() || undefined,
        client_id: client.id,
        container_id: container.id,
        unit_id: unit.id,
        require_fecha_vencimiento: parseExcelYesNo(
          row["Requiere fecha vencimiento"],
        ),
        is_standard_amount_per_container: parseExcelYesNo(
          row["Cantidad por contenedor estándar"],
        ),
        GroupArticles: groupIds.map((id) => ({ group_id: id })),
        fce: Number(row["FCE"]),
      });
    }

    return { transformed, errors };
  }

  const inputMimeType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";

  const readFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    setError(false);
    setListOfErrors([]);
    e.preventDefault();

    try {
      const file = e.target.files?.[0];
      if (!file) {
        setError(true);
        setLoading(false);
        return;
      }

      const reader = new FileReader();

      reader.onload = async (ev) => {
        try {
          const workbook = read(ev.target?.result as ArrayBuffer, {
            type: "array",
          });

          const target = normalize("articulos");

          const sheetEntry = Object.entries(workbook.Sheets).find(
            ([sheetName]) => {
              return normalize(sheetName) === target;
            },
          );

          if (!sheetEntry) {
            setError(true);
            setListOfErrors((prev) => [
              ...prev,
              `No se encontró la hoja "Articulos / Artículos / ARTICULOS".`,
            ]);
            setLoading(false);
            setModalOpen(true);
            return;
          }

          const [, sheet] = sheetEntry;

          const rawRows = utils.sheet_to_json(sheet, {
            defval: "",
          }) as NewArticleRow[];

          if (!rawRows.length) {
            setError(true);
            setListOfErrors([`La hoja no contiene filas para importar.`]);
            setLoading(false);
            setModalOpen(true);
            return;
          }

          const canonicalRows = rawRows.map((row) => {
            const out: Record<string, any> = {};
            for (const key in row) {
              const nk = normalizeHeader(key);
              const target = CANONICAL_MAP[nk];
              if (target) out[target] = row[key];
            }
            return out as NewArticleRow;
          });

          const normalizedSchema = normalizeSchemaKeys(articlesSchema);

          const normalizedRowsForValidation = canonicalRows.map((row) => {
            const r: Record<string, any> = {};
            for (const key in row) {
              r[normalizeHeader(key)] = row[key];
            }
            return r;
          });

          const result = validateSchema(
            normalizedRowsForValidation,
            normalizedSchema,
          );

          if (!result.isValid) {
            setListOfErrors(
              result.errors.map((err) => `ARTICULOS: ${err.message}`),
            );
            setError(true);
            setLoading(false);
            setModalOpen(true);
            return;
          }

          const { transformed, errors } =
            await createArticleStructure(canonicalRows);

          if (errors.length > 0) {
            setListOfErrors(errors);
            setError(true);
          } else {
            setUploadedArticles(transformed);
          }

          setModalOpen(true);
        } catch (ex: any) {
          setError(true);
          setListOfErrors((prev) => [
            ...prev,
            `Error procesando archivo: ${ex?.message}`,
          ]);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (ex: any) {
      setError(true);
      setListOfErrors((prev) => [...prev, ex.message]);
      setLoading(false);
    }
  };

  async function handleSubmit() {
    let hasBackendErrors = false;

    try {
      setLoading(true);
      setModalOpen(false);

      const resp = await createOrUpdateArticlesMassive(uploadedArticles);

      if (resp.status === STATUS_CODES.OK) {
        setLoading(false);
        setModalOpen(true);
        const { created, updated, failed, errors } = resp.data;

        if (created.length > 0) {
          for (const a of created) {
            addSessionData(ENTITY.ARTICLES, a);
          }
        }
        if (updated.length > 0) {
          for (const a of updated) {
            updateSessionData(ENTITY.ARTICLES, a);
          }
        }

        if (failed.length === 0) {
          showSnackBar(
            `Se crearon ${created.length} artículos y se actualizaron ${updated.length} artículos correctamente.`,
            "success",
          );
        } else {
          hasBackendErrors = true;
          setListOfErrors(errors);
          setError(true);

          showSnackBar(
            `Resultado: ${created.length} creados — ${updated.length} actualizados — ${failed.length} con errores.`,
            "warning",
          );
          return;
        }
      } else {
        showSnackBar(resp.data?.message ?? "Error inesperado", "error");
      }
    } catch (ex: unknown) {
      const message =
        ex instanceof Error
          ? ex.message
          : typeof ex === "string"
            ? ex
            : "Error inesperado.";

      showSnackBar(message, "error");
    } finally {
      setLoading(false);

      if (!hasBackendErrors) {
        setModalOpen(false);
        setResetInput(Math.random().toString(36));
        setUploadedArticles([]);
      }
    }
  }

  return (
    <>
      <Button
        component="label"
        variant="contained"
        startIcon={<MdCloudUpload />}
      >
        {loading ? <CircularProgress /> : "Cargar Artículos"}
        <VisuallyHiddenInput
          type="file"
          accept={inputMimeType}
          key={resetInput}
          ref={fileUploadRef}
          onChange={readFile}
        />
      </Button>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Paper
          sx={{
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            padding: "1rem",
            width: "500px",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Grid container rowGap={2}>
            {!error && uploadedArticles.length > 0 && (
              <>
                <Typography variant="h6">
                  Datos importados correctamente
                </Typography>
                <Typography>
                  Artículos a importar{" "}
                  <span style={{ color: "green" }}>
                    {uploadedArticles.length}
                  </span>{" "}
                  artículos
                </Typography>

                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    startIcon={<MdCancel />}
                    onClick={() => {
                      setModalOpen(false);
                      setUploadedArticles([]);
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<MdCheck />}
                    onClick={handleSubmit}
                  >
                    Cargar Datos
                  </Button>
                </Grid>
              </>
            )}

            {error && (
              <>
                <Typography variant="h5" color="error" textAlign="center">
                  Ocurrió un error
                </Typography>

                <Grid container justifyContent="center" mt={2}>
                  <Grid item xs={12} md={10} lg={8}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                        Se encontraron ({listOfErrors.length}) errores
                      </AccordionSummary>
                      <AccordionDetails>
                        {listOfErrors.map((err, i) => (
                          <Alert key={i} severity="error" sx={{ mb: 1 }}>
                            {err}
                          </Alert>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Modal>

      <Backdrop
        open={loading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          color: "#fff",
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
